import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "src/app/components/icon/Icon";
import Loader from "src/app/components/loader/Loader";
import { FORM_TYPES } from "src/app/modules/auth/constants";
import { ProjectSystem, ProjectSystemAdditionalParameter, SYSTEM_PARAMETER_TYPE } from "src/app/modules/calculator/types";
import { ROUTES, ROLE } from "src/config/constants";
import { AuthContext } from "src/contexts/AuthContext";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { PartnerResponse } from "src/core/models/partners/PartnerResponse";
import { ProjectsService } from "src/core/services/projects/ProjectsService";

import calonCss from "./Calculation.module.css";
import PartnerModal from "./components/PartnerModal";

function Header({
  selectedSystems,
  showAuthModal,
  showPartnerModal,
}: {
  selectedSystems: Array<ProjectSystem>;
  showAuthModal: (formType: string) => void;
  showPartnerModal: (isOpen: boolean) => void;
}) {
  const [partnerModalOpened, setPartnerModalOpened] = useState(false);
  const [projectSent, setProjectSent] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [partnerSelected, setPartnerSelected] = useState<PartnerResponse>();

  const authData = useContext(AuthContext);
  const modalData = useContext(ModalContext);

  useEffect(() => {
    showPartnerModal(partnerModalOpened);
  }, [partnerModalOpened, showPartnerModal]);

  const buildAdditionalParameters = (projectSystem: ProjectSystem) => {
    let res = Array<ProjectSystemAdditionalParameter>();
    if (projectSystem.requiredApplianceThickness > 0) {
      res.push({
        systemAdditionalParameterId: projectSystem?.system?.additionalParameters?.find((y) => y.parameterType === SYSTEM_PARAMETER_TYPE.ApplianceThickness)?.id,
        value: projectSystem.requiredApplianceThickness,
      });
    }
    return res;
  };

  const handleSaveCalculations = () => {
    setSendLoading(true);
    ProjectsService.save({
      projectName: "Spremljeni izračun",
      projectSystems: selectedSystems.map((x: ProjectSystem) => ({
        ...x,
        additionalParameters: buildAdditionalParameters(x),
      })),
    })
      .then((response: any) => {
        modalData?.triggerSuccess(response);
        setProjectSent(true);
      })
      .catch((e: ApiResult) => {
        modalData?.triggerError(e);
        setProjectSent(false);
      })
      .finally(() => {
        setSendLoading(false);
      });
  };

  return (
    <div>
      <h2 className={classNames("u-t-uppercase u-t-primary u-t-align-center", calonCss.totalCalcHeader)}>ukupan izračun</h2>
      <div className={classNames("u-col-12", "u-flex", calonCss.totalCalcHeaderContent)}>
        <div className="u-sm-col-12 u-col-8">
          <h3>Potrebni materijali za sve odabrane sustave</h3>
          {!projectSent && (
            <p className="u-col-9 u-sm-col-12 u-t-f18p">
              Za prikaz cijene i kontakt s našim partnerima, molimo vas da se prijavite. Ukoliko nemate račun, izraditi ga možete klikom na Registrirajte se.
            </p>
          )}
          {authData?.auth?.isVerified ? (
            <div className={classNames("u-flex", calonCss.totalCalcAuth)}>
              {projectSent ? (
                <div className={classNames("u-col-12 u-flex u-flex-items-center u-t-success", calonCss.totalCalcSent)}>
                  <div className="u-flex u-flex-items-center">
                    <Icon name="accept" className="u-t-f22p u-t-success"></Icon>
                    <span className="u-ml15px u-t-f18p">{partnerSelected ? "Upit poslan partneru " + partnerSelected?.name : "Izračun spremljen."}</span>
                  </div>
                  {authData?.auth?.role === ROLE.User && (
                    <Link
                      to={ROUTES.showUserProjects(authData.auth.id)}
                      className={classNames(
                        "u-t-valign-mid",
                        "u-t-nodecoration",
                        "u-t-w400",
                        "u-t-uppercase",
                        "u-t-f16p",
                        "u-ml30px",
                        "u-cursor-pointer",
                        calonCss.blueFillBtn
                      )}
                    >
                      Idi na moje projekte
                    </Link>
                  )}
                </div>
              ) : (
                <div className={classNames("u-flex", "u-col-12", calonCss.buttonWrap)}>
                  <button
                    onClick={() => setPartnerModalOpened(true)}
                    className={classNames("u-t-uppercase", "u-cursor-pointer", "u-font-family-inh", "u-t-f16p", "u-t-lh15", calonCss.signUpBtn)}
                  >
                    Pošalji izračun našim partnerima
                  </button>
                  {(authData?.auth?.role === ROLE.Admin || authData?.auth?.role === ROLE.RegionalAdmin) && (
                    <button
                      onClick={() => handleSaveCalculations()}
                      className={classNames("u-t-uppercase", "u-cursor-pointer", "u-font-family-inh", "u-t-f16p", "u-t-lh15", calonCss.saveBtn)}
                    >
                      spremi izračun
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={classNames("u-flex", calonCss.totalCalcUnAuth)}>
              <button
                className={classNames("u-t-uppercase", "u-cursor-pointer", "u-font-family-inh", "u-t-f16p", "u-t-lh15", calonCss.signUpBtn)}
                onClick={() => showAuthModal(FORM_TYPES.LOGIN)}
              >
                prijavi se i saznaj cijenu
              </button>
              <div className="u-flex u-wauto u-flex-items-center">
                <span className="u-t-f18p">Nemate račun?</span>
                <button
                  className="u-t-primary u-p0 u-pl10px u-t-underline u-cursor-pointer u-t-f18p u-font-family-inh"
                  onClick={() => showAuthModal(FORM_TYPES.REGISTER)}
                >
                  Registrirajte se
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={classNames("u-bg-background", "u-col-4", "u-sm-col-12", calonCss.totalCalcRemark)}>
          <h6 className="u-pt10px u-pl10px u-t-primary u-t-uppercase u-mt0 u-mb10px">napomene:</h6>
          <p className="u-pl10px u-pr10px u-t-primary u-mt0 u-mb10px u-t-f14p u-t-lh20">
            Izračun je informativnog karaktera, izračunate količine su okvirne bez dodatka za otpad i mogu se razlikovati od stvarnih.
          </p>
          <p className="u-pl10px u-pr10px u-t-primary u-mt0 u-mb10px u-t-f14p u-t-lh20">
            S odabranim trgovcem provjerite specifikaciju i odabrane sustave kako bi izračun bio personaliziran i u skladu s vašim potrebama. Dodatne napomene,
            upite možete unijeti prije slanja upita partneru.
          </p>
        </div>
        {partnerModalOpened && (
          <PartnerModal
            selectedSystems={selectedSystems}
            hideModal={() => setPartnerModalOpened(false)}
            handleProjectSent={setProjectSent}
            handlePartnerSelected={setPartnerSelected}
          />
        )}
      </div>
      {sendLoading && <Loader />}
    </div>
  );
}

export default Header;
